'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import {
	Add,
	Button,
	CloseCircle,
	cn,
	ImagePlaceholder,
	Input,
	Minus,
	Trash,
} from '@desoutter/uikit'
import useCart from '@/hooks/useCart'
import { search } from '@/lib/redpim'
import { Item } from '@/app/api/types/redpim.types'
import { GetAQuoteFormFragment } from './GetaquoteForm.generated'

export default function CartForm({
	getAQuoteFragment,
}: {
	getAQuoteFragment: GetAQuoteFormFragment
}) {
	const { cart, cartReduced, add, remove, getAmountPerProduct, removeProduct } =
		useCart()
	const [query, setQuery] = useState('')
	const [queryProducts, setQueryProducts] = useState<Item[]>([])

	const [message, setMessage] = useState('')

	useEffect(() => {
		console.log(cart)
	}, [cart])

	useEffect(() => {
		const url = new URL(document.location.toString())
		const params = new URLSearchParams(url.search)
		if (
			params.get('pn') &&
			!cart.some((product) => product.partnumber === params.get('pn'))
		)
			search({
				query: params.get('pn'),
				querytype: 'partnumber',
				locale: 'en_US',
			}).then(({ items }) => {
				if (items?.length) {
					add({
						model: items[0].model,
						partnumber: items[0].partnumber,
						thumbnail: items[0].thumbnail,
					} as Item)
				}
			})
	}, [])

	useEffect(() => {
		if (/^[a-zA-Z]/.test(query) && query.length > 2) {
			const getData = setTimeout(() => {
				search({
					query,
					querytype: 'model',
					locale: 'en_US',
				}).then(({ items }) => {
					if (items) setQueryProducts(items)
				})
			}, 1000)

			return () => clearTimeout(getData)
		}
		if (query.length > 3) {
			const getData = setTimeout(() => {
				search({
					query,
					querytype: 'partnumber',
					locale: 'en_US',
				}).then(({ items }) => {
					if (items) setQueryProducts(items)
				})
			}, 1000)

			return () => clearTimeout(getData)
		}
	}, [query])

	const addProduct = (product: Item) => {
		add(product)
	}

	const subtractProduct = (product: Item) => {
		remove(product)
	}

	const deleteFromCart = (product: Item) => {
		removeProduct(product)
	}

	return (
		<>
			<div className="p-8 rounded-3xl bg-gray-50">
				<p className="text-lg mb-4">{getAQuoteFragment.yourProductsLabel}</p>
				{cartReduced.map((product) => (
					<ProductRow
						deleteFromCart={deleteFromCart}
						add={addProduct}
						subtract={subtractProduct}
						key={product.partnumber}
						product={product}
						amount={getAmountPerProduct(product.partnumber)}
					/>
				))}
				<div
					className={cn('relative rounded-full bg-white gap-8', {
						'border border-gray-100': !!queryProducts.length,
					})}
				>
					<div className="relative flex items-center px-3 pr-7  z-10">
						<Input
							id="add_partnumber"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={getAQuoteFragment.querySearchPlaceholder}
							className="!w-full !mt-0 "
						/>
						<button
							className="cursor-pointer"
							onClick={() => {
								setQuery('')
								setQueryProducts([])
							}}
						>
							<CloseCircle />
						</button>
					</div>
					{!!queryProducts.length && (
						<div className="absolute top-8 w-full pt-8 bg-white border-l border-r border-b border-gray-100 rounded-b-3xl">
							<div className="h-[435px] custom-scrollbar scrollbar-w-[4px] overflow-y-auto m-2 border-t">
								{queryProducts.map((product) => (
									<div
										className="flex items-center px-3 py-3 border-b border-gray-50 "
										key={product.partnumber}
									>
										{product.thumbnail ? (
											<Image
												src={
													product.thumbnail +
													'/jcr:content/renditions/cq5dam.web.400.400.jpeg'
												}
												alt={product.model}
												width={64}
												height={64}
											/>
										) : (
											<ImagePlaceholder />
										)}
										<div
											className="flex items-center justify-between w-full ml-4 cursor-pointer"
											onClick={() => {
												setQuery('')
												setQueryProducts([])
												add({
													model: product.model,
													partnumber: product.partnumber,
													thumbnail: product.thumbnail,
												} as Item)
											}}
										>
											{product.model && (
												<div className="mr-4">
													<p className="text-lg font-bold">{product.model}</p>
													<p className="text-sm">{product.partnumber}</p>
												</div>
											)}
											<Button
												title="plus"
												color="tertiary"
												size="xs"
												icon={Add}
											/>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</>
	)
}

const ProductRow = ({
	deleteFromCart,
	product,
	amount,
	add,
	subtract,
}: {
	product: Item
	amount: number
	deleteFromCart: (item: Item) => void
	add: (item: Item) => void
	subtract: (item: Item) => void
}) => {
	return (
		<div className="grid grid-cols-6 mb-3 rounded-2xl bg-white gap-8">
			<div className="col-span-3 flex p-3">
				{product.thumbnail ? (
					<Image
						src={
							product.thumbnail +
							'/jcr:content/renditions/cq5dam.web.400.400.jpeg'
						}
						alt={product.model}
						width={64}
						height={64}
					/>
				) : (
					<ImagePlaceholder />
				)}
				<div className="ml-4">
					<p className="text-lg font-bold">{product.model}</p>
					<p className="text-sm">{product.partnumber}</p>
				</div>
			</div>
			<div className="relative col-span-2 flex items-center justify-between">
				<Button
					title="minus"
					className="w-14 h-14 !pl-4"
					variant="outline"
					color="tertiary"
					size="lg"
					onClick={() => subtract(product)}
					icon={Minus}
				/>
				<div className="px-4 text-lg">{amount}</div>
				<Button
					title="plus"
					className="w-14 h-14 !pl-4"
					variant="outline"
					color="tertiary"
					size="lg"
					onClick={() => add(product)}
					icon={Add}
				/>
			</div>
			<div className="flex items-center">
				<Button
					title="minus"
					color="tertiary"
					size="lg"
					onClick={() => deleteFromCart(product)}
					icon={Trash}
				/>
			</div>
		</div>
	)
}
